import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, } from 'react-router-dom';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import ReactDOMServer from 'react-dom/server';
import { bbox } from '@turf/turf';
import { toast } from 'react-toastify';
import Header from '../components/Header/Header';
import Button from '../components/Button/Button';
import MapViewer from '../components/MapViewer/MapViewer';
import {
    MapIcon,
    LogoutIcon,
    GlobeIcon, 
    LayersIcon, 
    DrawPolygonIcon,
    BuildingIcon,
    RoadIcon,
    RailroadIcon,
    BoundaryIcon,
    PinIcon,
    PlusIcon,
    MinusIcon,
    ViewIcon,
    XCircleIcon,
    ImportIcon,
    ExportIcon,
    RulerIcon,
    CrosshairIcon,
    ListIcon,
    DrawLineIcon,
    SettingsIcon,
    EditIcon,
    TextIcon,
    PrintIcon,
    ExcelIcon,
    InfoIcon
} from '../components/Icon';
import ThemeToggle from '../components/ThemeToggle/ThemeToggle';
import styles from './Pages.module.css';
import EditingPanel from '../components/EditingPanel/EditingPanel';
import LegendManager from '../components/LegendManager/LegendManager';
import CoordsInfoPanel from '../components/CoordsInfoPanel/CoordsInfoPanel';
import ObjectManagerPanel from '../components/ObjectManagerPanel/ObjectManagerPanel';
import ActionToolbar from '../components/ActionToolbar/ActionToolbar';
import ImportModal from '../components/ImportModal/ImportModal';
import { modules } from '../generated-modules';

// Поиск компонентов модулей
const nspdModule = modules.find(m => m.id === 'nspd-integration');
const spdPlusModule = modules.find(m => m.id === 'spd-plus');
const NSPDUpdateModal = nspdModule?.UpdateModal || (() => null);
const isSPDPlusActive = !!spdPlusModule;

// Динамический импорт контекстов модулей (если проект сгенерирован)
let usePrinting = () => ({ settings: null });
try {
    // Используем динамический путь через переменную, чтобы webpack не ругался при отсутствии файла
    const modulePath = '../modules-copy/printing/context/PrintingContext';
    const printingModule = require(`${modulePath}`);
    if (printingModule) usePrinting = printingModule.usePrinting;
} catch (e) {
    // Модуль не установлен или еще не сгенерирован
}

const API_URL = 'http://localhost:3001/api';

// Auth check wrapper - checks auth before rendering component
const useAuthCheck = () => {
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoadingAuth, setIsLoadingAuth] = useState(true);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const response = await fetch(`${API_URL}/auth/check`, { 
                    credentials: 'include' 
                });
                const data = await response.json();
                if (!data.authenticated) {
                    navigate('/map/admin', { replace: true });
                } else {
                    setIsAuthenticated(true);
                }
            } catch (err) {
                console.error('Auth check failed:', err);
                navigate('/map/admin', { replace: true });
            } finally {
                setIsLoadingAuth(false);
            }
        };
        checkAuth();
    }, [navigate]);

    return { isAuthenticated, isLoadingAuth };
};

// Auth guard component
const AuthGuard = ({ children }) => {
    const { isAuthenticated, isLoadingAuth } = useAuthCheck();

    if (isLoadingAuth) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh' 
            }}>
                <p>Проверка авторизации...</p>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    return children;
};

const AdminDashboardPageInner = () => {
    const navigate = useNavigate();
    const { settings: printingSettings } = usePrinting();

    // Собираем иконки для передачи в модули (нужно определить раньше для getTools)
    const iconComponents = {
        Map: MapIcon, Logout: LogoutIcon, Globe: GlobeIcon, Layers: LayersIcon, 
        DrawPolygon: DrawPolygonIcon, Building: BuildingIcon, Road: RoadIcon, 
        Railroad: RailroadIcon, Boundary: BoundaryIcon, Pin: PinIcon, Plus: PlusIcon, 
        Minus: MinusIcon, Eye: ViewIcon, EyeOff: XCircleIcon, Import: ImportIcon, 
        Export: ExportIcon, Ruler: RulerIcon, Crosshair: CrosshairIcon, List: ListIcon, 
        DrawLine: DrawLineIcon, Settings: SettingsIcon, Edit: EditIcon, Text: TextIcon, 
        Print: PrintIcon, Excel: ExcelIcon, Info: InfoIcon
    };

    const [activeMode, setActiveMode] = useState('data');
    const [activeTool, setActiveTool] = useState(null);
    const [mapStyleId, setMapStyleId] = useState('custom'); // Изменено на 'custom' по умолчанию
    const [currentMapViewState, setCurrentMapViewState] = useState({
        longitude: parseFloat(process.env.REACT_APP_MAP_LON) || 60.180497,
        latitude: parseFloat(process.env.REACT_APP_MAP_LAT) || 56.469595,
        zoom: parseFloat(process.env.REACT_APP_MAP_ZOOM) || 15.5,
        pitch: parseFloat(process.env.REACT_APP_MAP_PITCH) || 3.5,
        bearing: parseFloat(process.env.REACT_APP_MAP_BEARING) || 62.5
    });
    const [previousMapViewState, setPreviousMapViewState] = useState(null);
    const [restoreMapViewState, setRestoreMapViewState] = useState(null);
    const [editingFeature, setEditingFeature] = useState(null);
    const [featureToEditGeometry, setFeatureToEditGeometry] = useState(null);
    const [featureBeforeEdit, setFeatureBeforeEdit] = useState(null); // Сохраняем исходный объект
    const [drawingControls, setDrawingControls] = useState(null); // State to hold drawing controls from MapViewer
    const mapRef = useRef(null);
    const [featureToCancel, setFeatureToCancel] = useState(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [legendData, setLegendData] = useState({
        plots: [],
        buildings: [],
        roads: [],
        markers: []
    });
    const [featureProperties, setFeatureProperties] = useState({});
    // Инициализируем mapData с пустой структурой, чтобы избежать null
    const [mapData, setMapData] = useState({
        type: "FeatureCollection",
        features: []
    });
    const [isLegendManagerVisible, setLegendManagerVisible] = useState(false);
    const [coordsInfo, setCoordsInfo] = useState(null); // Состояние для панели с информацией о координатах
    const [nspdInfo, setNspdInfo] = useState(null); // Состояние для результатов поиска в НСПД по координатам
    const [isNSPDModalVisible, setNSPDModalVisible] = useState(false);
    const [isNSPDUpdateModalOpen, setNSPDUpdateModalOpen] = useState(false);
    const [isObjectManagerVisible, setObjectManagerVisible] = useState(false);
    const [initialOverlay, setInitialOverlay] = useState(null);
    const allModes = ['drawing', 'data'];
    // Динамически добавляем режимы из модулей
    modules.forEach(mod => {
        if (mod.getTools) {
            const modTools = mod.getTools(iconComponents); 
            Object.keys(modTools).forEach(mode => {
                if (!allModes.includes(mode)) {
                    allModes.push(mode);
                }
            });
        }
    });
    const [isImportModalVisible, setImportModalVisible] = useState(false);
    const [featuresToImport, setFeaturesToImport] = useState([]);
    const [mapSelectedIds, setMapSelectedIds] = useState([]); // Состояние для выделения на карте через Shift
    const importInputRef = useRef(null);

    const mobileModes = ['data'];
    const modes = isMobile ? mobileModes : allModes;
    const modeNames = { drawing: 'Рисование', data: 'Данные' };
    
    // Добавляем имена режимов из модулей
    modules.forEach(mod => {
        if (mod.getTools) {
            const modTools = mod.getTools(iconComponents);
            Object.keys(modTools).forEach(modeKey => {
                if (mod.name && !modeNames[modeKey]) {
                    modeNames[modeKey] = mod.name;
                }
            });
        }
    });

    // --- Управление видимостью объектов ---
    const getSavedVisibilities = useCallback(() => {
        try {
            const saved = localStorage.getItem('featureVisibilities');
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            console.error("Failed to parse feature visibilities from localStorage", e);
            return {};
        }
    }, []);
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            // Если мы на мобильном и активен режим "Рисование", переключаемся на "Данные"
            if (mobile && activeMode === 'drawing') {
                setActiveMode('data');
                setActiveTool(null);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [activeMode]);

    useEffect(() => {
        // Загружаем сохраненные данные из IndexedDB
        const loadOverlay = async () => {
            try {
                const DB_NAME = 'SPD_Overlay_DB';
                const STORE_NAME = 'overlay_store';
                const DB_KEY = 'current_overlay';
                
                const dbRequest = indexedDB.open(DB_NAME, 1);
                dbRequest.onsuccess = (e) => {
                    const db = e.target.result;
                    if (db.objectStoreNames.contains(STORE_NAME)) {
                        const tx = db.transaction(STORE_NAME, 'readonly');
                        const store = tx.objectStore(STORE_NAME);
                        const getRequest = store.get(DB_KEY);
                        getRequest.onsuccess = (ev) => {
                            if (ev.target.result) {
                                setInitialOverlay(ev.target.result);
                            }
                        };
                    }
                };
            } catch (e) {
                console.error("Failed to load overlay from IndexedDB", e);
            }
        };
        loadOverlay();
    }, []);

    const fetchMetadata = useCallback(async () => {
        try {
            console.log('Fetching metadata...');
            const [legendRes, propsRes, mapDataRes] = await Promise.all([
                fetch('/api/legend'),
                fetch('/api/feature-properties'), // Свойства для панели редактирования
                fetch('/api/map') // Основные данные карты (объекты)
            ]);

            let newLegendData = legendData; // Сохраняем текущую легенду на случай ошибки
            if (legendRes.ok) {
                newLegendData = await legendRes.json();
                setLegendData(newLegendData);
            }

            if (propsRes.ok) {
                const props = await propsRes.json();
                setFeatureProperties(props);
            }

            if (mapDataRes.ok) {
                const mapDataPayload = await mapDataRes.json();
                console.log('Raw mapDataPayload:', mapDataPayload);
                if (mapDataPayload?.features) {
                    console.log('Raw features count:', mapDataPayload.features.length);
                    mapDataPayload.features.forEach((f, i) => {
                        console.log(`Raw feature ${i}: id=${f.id}, type=${f.properties?.type}, name=${f.properties?.name}`);
                    });
                }
                 
                 // Создаем справочников для быстрого доступа к свойствам легенды
                 const categoryProperties = { plot: {}, building: {}, road: {}, line: {}, marker: {} };
                 
                  const mapToDict = (dict, list) => (list || []).forEach(c => { if(c.id) dict[String(c.id)] = c; });
                  mapToDict(categoryProperties.plot, newLegendData.plots);
                  mapToDict(categoryProperties.building, newLegendData.buildings);
                  mapToDict(categoryProperties.road, newLegendData.roads);
                  mapToDict(categoryProperties.line, newLegendData.roads);
                  mapToDict(categoryProperties.marker, newLegendData.markers);

                 // Обновляем свойства объектов в соответствии с новой легендой
                 const updatedFeatures = mapDataPayload.features.map(feature => {
                    // --- Ключевое исправление ---
                    // Гарантируем, что у каждого объекта есть `id` в `properties`.
                    // `promoteId` в MapLibre требует, чтобы ID был именно в `properties`.
                    if (feature.id && feature.properties.id !== feature.id) {
                        feature.properties.id = feature.id;
                    }
                    // --- Конец исправления ---

                     const rawCat = feature.properties.category || feature.properties.marker_id || feature.properties.markerId;
                     const categoryId = (rawCat && typeof rawCat === 'object') ? rawCat.id : rawCat;
                     
                     let type = feature.properties.type;
                     if (!type && feature.id) {
                         if (feature.id.startsWith('plot')) type = 'plot';
                         else if (feature.id.startsWith('building')) type = 'building';
                         else if (feature.id.startsWith('road')) type = 'road';
                         else if (feature.id.startsWith('marker')) type = 'marker';
                     }

                     let newProperties = { ...feature.properties };

                     const props = (categoryId && type && categoryProperties[type]) ? categoryProperties[type][String(categoryId)] : null; if (props) {
                         // Устанавливаем значения с дефолтами, если props.* null/undefined
                         newProperties = {
                             ...newProperties,
                             color: props.color,
                             icon_filename: props.image_filename, // Присваиваем имя файла иконки
                             lineType: (props.lineType !== null && props.lineType !== undefined) ? props.lineType :
                                       (type === 'road' || type === 'line') ? 'solid' : null,
                             hatch: (props.hatch !== null && props.hatch !== undefined) ? props.hatch :
                                    (type === 'plot' || type === 'building') ? 'none' : null
                         };
                     } else {
                         newProperties.color = '#808080'; // Цвет по умолчанию, если категория не найдена
                         // Устанавливаем дефолтные значения для lineType и hatch в зависимости от типа
                         if (type === 'road' || type === 'line') {
                             newProperties.lineType = 'solid';
                             newProperties.hatch = null;
                         } else if (type === 'plot' || type === 'building') {
                             newProperties.hatch = 'none';
                             newProperties.lineType = null;
                         } else if (type === 'marker') {
                            newProperties.icon_filename = null; // Сбрасываем, если категория не найдена
                         } 
                         // Для markers ничего не устанавливаем дополнительно
                     }
                     return { ...feature, properties: newProperties, id: feature.id };
                 });

                // Применяем сохраненные пользователем настройки видимости
                const savedVisibilities = getSavedVisibilities();
                const featuresWithVisibility = updatedFeatures.map(feature => {
                    if (savedVisibilities.hasOwnProperty(feature.id)) {
                        return {
                            ...feature,
                            properties: {
                                ...feature.properties,
                                visible: savedVisibilities[feature.id]
                            }
                        };
                    }
                    return feature;
                });
                // Устанавливаем обновленные данные карты
                setMapData({ ...mapDataPayload, features: featuresWithVisibility });
            }
        } catch (error) {
            console.error('Ошибка загрузки метаданных:', error);
        }
    }, []); // Зависимости не нужны, т.к. функции-сеттеры состояния стабильны

    // Загрузка легенды и метаданных свойств
    useEffect(() => {
        fetchMetadata();
    }, [fetchMetadata, legendData]);

    const mapStyles = ['basic', 'satellite', 'custom'];
    const mapStyleNames = { basic: 'Схема', satellite: 'Спутник', custom: 'Своя' };
    // Генерируем SVG-строки из React-компонентов
    const mapStyleIcons = {
        basic: ReactDOMServer.renderToStaticMarkup(<MapIcon size={18} />),
        satellite: ReactDOMServer.renderToStaticMarkup(<GlobeIcon size={18} />),
        custom: ReactDOMServer.renderToStaticMarkup(<LayersIcon size={18} />)
    };

    const handleMapStyleChange = () => {
        const currentIndex = mapStyles.indexOf(mapStyleId);
        const nextIndex = (currentIndex + 1) % mapStyles.length;
        setMapStyleId(mapStyles[nextIndex]);
    };

    const handleModeChange = () => {
        const currentIndex = modes.indexOf(activeMode);
        const nextIndex = (currentIndex + 1) % modes.length;
        setActiveMode(modes[nextIndex]);
        setActiveTool(null); // Сбрасываем активный инструмент при смене режима
    };

    const handleExport = () => {
        if (!mapData || !mapData.features) {
            toast.warn('Нет данных для экспорта.');
            return;
        }

        const visibleFeatures = mapData.features.filter(
            f => f.properties.visible !== false
        );

        const dataToExport = {
            type: 'FeatureCollection',
            features: visibleFeatures,
            // Статусы (for_sale, is_building, is_planned) уже будут в properties объектов
            // Их не нужно явно добавлять на верхний уровень
        };

        const jsonString = JSON.stringify(dataToExport, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'map_data.json';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success(`Экспортировано ${visibleFeatures.length} объектов.`);
    };

    const handleImportClick = () => {
        importInputRef.current?.click();
    };

    const handleFileImport = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                if (data && data.type === 'FeatureCollection' && Array.isArray(data.features)) {
                    setFeaturesToImport(data.features);
                    setImportModalVisible(true);
                } else {
                    toast.error('Неверный формат файла. Ожидается GeoJSON FeatureCollection.');
                }
            } catch (error) {
                toast.error('Ошибка при чтении файла: ' + error.message);
            }
        };
        reader.readAsText(file);
        // Сбрасываем значение инпута, чтобы можно было выбрать тот же файл еще раз
        event.target.value = null;
    };

    const handleToolChange = (toolId) => {
        // Обработка кнопки "Помощь" - открытие документации в новой вкладке
        if (toolId === 'help') {
            window.open('/map/admin/documentation', '_blank');
            return;
        }

        // Новая логика: если кликаем на активный инструмент измерения во время редактирования,
        // просто закрываем панель редактирования, но оставляем инструмент активным.
        if (editingFeature && (toolId === 'measure-ruler' || toolId === 'measure-area') && activeTool === toolId) {
            closeAllPanels();
            // Не меняем activeTool, чтобы он остался активным
            return;
        }

        // Определяем, является ли клик первым нажатием на инструмент измерения во время редактирования
        const isFirstMeasureClickWhileEditing = editingFeature && (toolId === 'measure-ruler' || toolId === 'measure-area');

        // Очищаем карту перед сменой инструмента
        if (drawingControls) { // Use the drawingControls state
            drawingControls.clearDrawing(); // Call clearDrawing from the received controls
        }
        // Если мы редактируем объект и переключаемся на инструмент, НЕ являющийся измерительным, отменяем редактирование.
        if (editingFeature && !isFirstMeasureClickWhileEditing) {
            closeAllPanels();
        }

        // Специальная обработка для инструмента легенды
        if (toolId === 'legend') {
            setLegendManagerVisible(true);
            return; // Не устанавливаем activeTool, так как это модальное окно
        }
        // Специальная обработка для менеджера объектов
        if (toolId === 'object-manager') {
            setObjectManagerVisible(prev => !prev);
            return; // Не устанавливаем activeTool
        }

        if (toolId === 'export') {
            handleExport();
            setActiveTool(null); // Экспорт - это одноразовое действие
            return;
        }

        if (toolId === 'import') {
            handleImportClick();
            setActiveTool(null); // Импорт - это одноразовое действие
            return;
        }

        if (toolId === 'nspd-search') {
            setNSPDModalVisible(true);
            return;
        }

        // Если кликаем на уже активный инструмент, деактивируем его, иначе — активируем новый
        setActiveTool(prevTool => prevTool === toolId ? null : toolId);
        // Если мы деактивируем инструмент координат, закрываем панель
        if (activeTool === 'measure-coords' && activeTool === toolId) {
            setCoordsInfo(null);
        }
    };

    // Навигация Header для админки
    const navItems = [
        {
            id: 'mode-switcher',
            label: modeNames[activeMode],
            active: true,
            sublabel: 'Режим',
            onClick: handleModeChange
        }
    ];

    // Действия Header
    const headerActions = [
        { 
          id: 'theme-toggle',
          component: <ThemeToggle /> 
        },
        {
            id: 'logout', // Изменено на иконку
            icon: <LogoutIcon size={20} />,
            title: 'Выйти из системы',
            onClick: async () => {
                if (window.confirm('Выйти из системы?')) {
                    try {
                        await fetch(`${API_URL}/auth/logout`, {
                            method: 'POST',
                            credentials: 'include'
                        });
                    } catch (err) {
                        console.error('Logout error:', err);
                    }
                    navigate('/map/admin');
                }
            }
        }
    ];


    // Инструменты для Toolbar в зависимости от режима
    let toolsConfig = {
        drawing: [
            {
                label: 'Рисование',
                items: [
                    { id: 'draw-plot', label: 'Участки', icon: <DrawPolygonIcon size={18} />, title: 'Рисовать участки' },
                    { id: 'draw-building', label: 'Здания', icon: <BuildingIcon size={18} />, title: 'Рисовать здания' },
                    { id: 'draw-line', label: 'Линии', icon: <DrawLineIcon size={18} />, title: 'Рисовать линейные объекты' },
                    { id: 'draw-marker', label: 'Метки', icon: <PinIcon size={18} />, title: 'Ставить метки' },
                ]
            },
            {
                label: 'Подложка',
                items: [
                    { id: 'basemap', label: 'Подложка', icon: <LayersIcon size={18} />, title: 'Управление подложкой' },
                ]
            },
            {
                label: 'Данные',
                items: [
                    { id: 'import', label: 'Импорт', icon: <ImportIcon size={18} />, title: 'Импорт данных' },
                    { id: 'export', label: 'Экспорт', icon: <ExportIcon size={18} />, title: 'Экспорт данных' },
                ]
            }
        ],
        data: [
            {
                label: 'Измерения',
                items: [
                    { id: 'measure-ruler', label: 'Линейка', icon: <RulerIcon size={18} />, title: 'Измерить расстояние' },
                    { id: 'measure-area', label: 'Площадь', icon: <DrawPolygonIcon size={18} />, title: 'Измерить площадь' },
                    { id: 'measure-coords', label: 'Координаты', icon: <CrosshairIcon size={18} />, title: 'Координаты точки' },
                ]
            },
            {
                label: 'Работа с данными',
                items: [
                    { id: 'legend', label: 'Легенда', icon: <ListIcon size={18} />, title: 'Легенда' },
                    { id: 'object-manager', label: 'Менеджер объектов', icon: <SettingsIcon size={18} />, title: 'Менеджер объектов' },
                ]
            }
        ]
    };

    // Внедряем инструменты из динамических модулей
    modules.forEach(mod => {
        if (mod.getTools) {
            const modTools = mod.getTools(iconComponents);
            Object.keys(modTools).forEach(mode => {
                if (!toolsConfig[mode]) {
                    toolsConfig[mode] = modTools[mode];
                } else {
                    // Если режим уже существует, объединяем группы инструментов
                    toolsConfig[mode] = [...toolsConfig[mode], ...modTools[mode]];
                }
            });
        }
    });

    // Добавляем группу "Справка" во все режимы
    const helpGroup = {
        label: 'Справка',
        items: [
            {
                id: 'help',
                label: 'Помощь',
                icon: <InfoIcon size={24} />,
                title: 'Помощь'
            },
        ]
    };
    
    // Добавляем подсветку для кнопки менеджера объектов
    if (toolsConfig.data[1] && toolsConfig.data[1].items) {
        const objectManagerButton = toolsConfig.data[1].items.find(item => item.id === 'object-manager');
        if (objectManagerButton) objectManagerButton.active = isObjectManagerVisible;
    }
    Object.keys(toolsConfig).forEach(mode => {
        toolsConfig[mode].push(helpGroup);
    });

    const currentMapViewStateRef = React.useRef(currentMapViewState);
    currentMapViewStateRef.current = currentMapViewState;

    const handleFeatureCreate = useCallback((feature) => { // useCallback уже был, но проверим зависимости
        // --- Исправление: Принудительно устанавливаем тип для новых объектов ---
        if (activeTool && feature.properties) {
            if (activeTool.startsWith('draw-')) {
                const type = activeTool.split('-')[1]; // 'plot', 'building', 'line', 'marker'
                feature.properties.type = type;
            }
        }

        console.log('Feature Created (raw):', feature);
        console.log('Feature id:', feature.id, 'properties.id:', feature.properties?.id);
        setPreviousMapViewState(currentMapViewStateRef.current);
        setEditingFeature(feature);
        setRestoreMapViewState(null); // Сбрасываем флаг восстановления
        setActiveTool(null); // Сбрасываем инструмент после создания
    }, [activeTool]); // Добавляем зависимость от activeTool

    const previousMapViewStateRef = React.useRef(previousMapViewState);
    previousMapViewStateRef.current = previousMapViewState;

    const handleEditingSave = useCallback((updatedFeature) => {
        console.log('Saving feature - full object:', JSON.stringify(updatedFeature));
        console.log('Saving feature - photos:', JSON.stringify(updatedFeature.properties?.photos));
        console.log('Saving feature - links:', JSON.stringify(updatedFeature.properties?.links));
        console.log('Saving feature - custom_fields:', JSON.stringify(updatedFeature.properties?.custom_fields));

        // Создаем копию объекта для отправки
        const featureToSave = JSON.parse(JSON.stringify(updatedFeature));

        // Если у объекта нет категории, назначаем ему первую доступную из легенды
        const isNewFeature = !mapData?.features.some(f => f.id === featureToSave.id);
        if (isNewFeature && !featureToSave.properties.category && legendData) {
            const type = featureToSave.properties.type;
            let categories = [];
            if (type === 'plot' || type === 'plot') categories = legendData.plots || [];
            else if (type === 'building') categories = legendData.buildings || [];
            else if (type === 'line' || type === 'road') categories = legendData.roads || [];
            else if (type === 'marker') categories = legendData.markers || [];

            if (categories.length > 0) {
                featureToSave.properties.category = categories[0].id;
            }
        }

        // Определяем, является ли объект обновлением существующего (есть ли он в mapData) или созданием нового
        const isUpdate = mapData?.features?.some(f => f.id === featureToSave.id);
        const url = isUpdate ? `/api/feature/${featureToSave.id}` : '/api/feature';
        const method = isUpdate ? 'PUT' : 'POST';

        // Отправляем объект на сервер
        fetch(url, {
            method: method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(featureToSave)
        })
        .then(response => {
            if (!response.ok) {
                throw new Error('Ошибка сохранения');
            }
            return response.json();
        })
        .then(data => {
            console.log('Feature saved:', data);
            // Очищаем drawRef после успешного сохранения
            if (drawingControls) {
                drawingControls.clearDrawing();
            }
            setEditingFeature(null);
            setFeatureToEditGeometry(null);
            setRestoreMapViewState(previousMapViewState); // Запускаем восстановление предыдущего состояния
            setPreviousMapViewState(null);
            // Обновляем данные карты в состоянии, что вызовет перерисовку MapViewer
            setMapData(prevData => {
                const prevFeatures = prevData?.features || [];
                const existingIndex = prevFeatures.findIndex(f => f.id === data.feature.id);
                let newFeatures;
                if (existingIndex > -1) {
                    newFeatures = [...prevFeatures];
                    newFeatures[existingIndex] = data.feature;
                } else {
                    newFeatures = [...prevFeatures, data.feature];
                }
                // Сохраняем другие поля (overlay, type) из prevData, если они есть; иначе создаём базовый объект
                return {
                    ...(prevData || { type: "FeatureCollection", features: [], overlay: null }),
                    features: newFeatures
                };
            });
        })
        .catch(error => {
            console.error('Ошибка при сохранении:', error);
            toast.error('Не удалось сохранить объект');
        });
    }, [previousMapViewState, drawingControls, legendData, mapData?.features]); // Добавлена зависимость от mapData.features

    const handleImportConfirm = async (selectedFeatures) => {
        if (!selectedFeatures || selectedFeatures.length === 0) {
            toast.warn('Не выбрано ни одного объекта для импорта.');
            return;
        }

        // Генерируем новые ID для импортируемых объектов, чтобы избежать конфликтов
        const featuresWithNewIds = selectedFeatures.map(feature => ({
            ...feature,
            id: crypto.randomUUID(),
            properties: {
                ...feature.properties,
                id: undefined, // Удаляем старый ID из свойств, если он там был
            }
        }));

        try {
            const response = await fetch('/api/features/bulk', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ features: featuresWithNewIds })
            });

            if (!response.ok) {
                let errorMessage = `Ошибка на сервере: ${response.status} ${response.statusText}`;
                try {
                    // Попытка прочитать ошибку в формате JSON
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                } catch (e) {
                    // Если ответ не в формате JSON, просто используем текстовое сообщение
                }
                throw new Error(errorMessage);
            }

            await response.json(); // Получаем ответ от сервера
            toast.success(`Успешно импортировано ${featuresWithNewIds.length} объектов.`);
            
            // Перезагружаем все данные с сервера, чтобы получить актуальное состояние
            await fetchMetadata();

        } catch (error) {
            console.error('Ошибка при импорте:', error);
            toast.error(`Не удалось импортировать объекты: ${error.message}`);
        }
    };

    const handleFeatureUpdate = useCallback((updatedFeature) => {
        console.log('Updating geometry for feature:', updatedFeature);
        // После перерисовки геометрии, мы обновляем объект в состоянии и открываем панель свойств
        const originalFeature = featureBeforeEdit;
        if (!originalFeature) return;

        // Создаем новый объект с новой геометрией, но старыми свойствами
        const featureWithNewGeometry = {
            ...originalFeature,
            geometry: updatedFeature.geometry,
        };

        setFeatureToEditGeometry(null); // Выходим из режима редактирования геометрии
        setEditingFeature(featureWithNewGeometry); // Открываем панель редактирования с обновленной геометрией
        setFeatureBeforeEdit(null); // Очищаем временное состояние
 
        // Не нужно обновлять mapData здесь, так как editingFeature вызовет
        // перерисовку MapViewer, который будет использовать focusedFeature для отображения
        // обновленного объекта, а старый объект уже был удален из mapData
        // в handleEditGeometry.
    }, [featureBeforeEdit]); // Зависимость корректна

    // handleFeatureDelete не используется напрямую в MapViewer, но для консистентности
    const handleFeatureDelete = useCallback(() => console.log('Feature Deleted'), []); 

    const handleOverlayUpdate = useCallback((newOverlayData) => { // Renamed for clarity
        console.log('Overlay Updated', newOverlayData);
        // Здесь в будущем будет логика сохранения подложки на бэкенде
    }, []);

    const handleFeatureSelectForEdit = useCallback((feature) => {
        console.log('Feature selected for editing (raw):', feature);
        // --- Детальное логирование для отладки ---
        console.log('Raw feature.id:', feature.id);
        console.log('Raw feature.properties:', feature.properties);
        console.log('Raw feature.layer:', feature.layer);
        // --- Конец логирования ---

        // `feature.id` теперь должен быть правильным благодаря `promoteId`.
        // `feature.properties.id` также должен быть, но `feature.id` надежнее.
        let featureId = feature.id || feature.properties?.id;

        // Если ID все еще отсутствует, генерируем временный (для новых, еще не сохраненных объектов).
        if (!featureId) {
            featureId = crypto.randomUUID();
            console.warn('Feature ID not found, generated a new one:', featureId);
        }

        const cleanFeature = {
            type: 'Feature',
            geometry: feature.geometry,
            properties: { ...feature.properties, id: featureId }, // Гарантируем наличие ID в properties
            id: featureId // И на верхнем уровне
        };

        // Сохраняем вид карты, только если панель редактирования была закрыта (т.е. editingFeature был null)
        if (!editingFeature) {
            setPreviousMapViewState(currentMapViewStateRef.current);
        }
        setEditingFeature(cleanFeature);
        console.log('Feature selected for editing (clean):', cleanFeature);
    }, [editingFeature]); // Зависимость от editingFeature нужна для корректного сохранения previousMapViewState

    // Выбор объекта из менеджера объектов
    const handleFeatureSelectFromManager = useCallback((feature) => {
        // Открываем панель редактирования
        handleFeatureSelectForEdit(feature);
        // Центрируемся на объекте
        const map = mapRef.current?.getMap();
        if (map && feature.geometry) {
            const featureBbox = bbox(feature);
            map.fitBounds(featureBbox, {
                padding: { top: 100, bottom: 100, left: 400, right: 400 }, // Отступы для панелей
                duration: 1000
            });
        }
    }, [handleFeatureSelectForEdit]);

    // Переключение видимости объекта из менеджера
    const handleFeatureVisibilityChange = useCallback((featureId, isVisible) => {
        const visibilities = getSavedVisibilities();
        visibilities[featureId] = isVisible;
        localStorage.setItem('featureVisibilities', JSON.stringify(visibilities));

        setMapData(prevData => {
            if (!prevData || !prevData.features) return prevData;

            const updatedFeatures = prevData.features.map(f => {
                if (f.id === featureId) {
                    // Создаем новый объект properties, чтобы избежать мутации
                    const newProperties = { ...f.properties, visible: isVisible };
                    return { ...f, properties: newProperties };
                }
                return f;
            });

            return {
                ...prevData,
                features: updatedFeatures,
            };
        });
    }, [getSavedVisibilities]);

    // Массовое переключение видимости объектов из менеджера
    const handleBulkVisibilityChange = useCallback((featureIds, isVisible) => {
        const visibilities = getSavedVisibilities();
        featureIds.forEach(id => {
            visibilities[id] = isVisible;
        });
        localStorage.setItem('featureVisibilities', JSON.stringify(visibilities));

        setMapData(prevData => {
            if (!prevData || !prevData.features) return prevData;

            const idSet = new Set(featureIds);
            const updatedFeatures = prevData.features.map(f => {
                if (idSet.has(f.id)) {
                    return { ...f, properties: { ...f.properties, visible: isVisible } };
                }
                return f;
            });

            return { ...prevData, features: updatedFeatures };
        });
        toast.info(`Видимость ${featureIds.length} объектов обновлена`);
    }, [getSavedVisibilities]);

    // Функция для закрытия всех панелей
    const closeAllPanels = useCallback(() => {
        console.log('Closing all panels / Cancelling edit');
        // Если мы отменяем редактирование геометрии, восстанавливаем исходный объект на карте
        if (featureBeforeEdit) {
            setMapData(prevData => {
                const newFeatures = [...prevData.features, featureBeforeEdit];
                return { ...prevData, features: newFeatures };
            });
        }

        if (drawingControls) {
            drawingControls.clearDrawing();
        }
        setEditingFeature(currentFeature => {
            if (currentFeature && !currentFeature.properties.id) setFeatureToCancel(currentFeature.id);
            return null;
        });
        setFeatureBeforeEdit(null);
        setFeatureToEditGeometry(null);
        setRestoreMapViewState(previousMapViewState); // Запускаем восстановление вида карты
    }, [drawingControls, previousMapViewState]);

    const handleDeleteFeature = useCallback(() => {
        if (!editingFeature) return;

        const objectName = editingFeature.properties.name || editingFeature.id;
        if (!window.confirm(`Вы уверены, что хотите удалить объект "${objectName}"?`)) {
            return;
        }

        // Если у объекта нет ID, он не может быть удален с сервера.
        // Это может произойти только если объект создается, но еще не сохранен.
        if (!editingFeature.id) {
            toast.warn('Нельзя удалить несохраненный объект.');
            return;
        }

        // Отправляем запрос на удаление на сервер для любого объекта с ID.
        // Это включает и старые, и новые объекты.
        fetch(`/api/feature/${editingFeature.id}`, { method: 'DELETE' })
            .then(response => {
                if (!response.ok) throw new Error('Ошибка удаления');
                return response.json();
            })
            .then(() => {
                toast.success('Объект успешно удален');
                closeAllPanels();
                fetchMetadata(); // Перезагружаем данные
            })
            .catch(error => {
                console.error('Ошибка при удалении:', error);
                toast.error('Не удалось удалить объект');
            });
    }, [editingFeature, mapData, closeAllPanels, fetchMetadata, drawingControls]);

    const handleEditGeometry = useCallback(() => {
        if (!editingFeature) return;
        console.log('Start editing geometry for:', editingFeature.id);
        setFeatureBeforeEdit(editingFeature); // Сохраняем исходное состояние
        setFeatureToEditGeometry(editingFeature);
        setEditingFeature(null); // Закрываем панель свойств
    }, [editingFeature]);

    // Эффект для отмены редактирования по клавише Escape
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeAllPanels();
            }
        };

        if (editingFeature || featureToEditGeometry) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [editingFeature, featureToEditGeometry, closeAllPanels]);

    // Эффект для деактивации инструмента по клавише Escape
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && activeTool) {
                setActiveTool(null);
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeTool]);

    // Обработчик для инструмента "Координаты"
    const handleCoordsSelect = useCallback((info) => {
        setCoordsInfo(info);
        // Фокусируемся на точке с отступом справа для панели
        const map = mapRef.current?.getMap();
        if (map) {
            map.easeTo({
                center: info.coordinates,
                zoom: Math.max(map.getZoom(), 17), // Приближаемся, если карта далеко
                padding: { right: 380 }, // Отступ для панели (360px + 20px)
                duration: 1000
            });
        }
    }, []);

    const handleCoordsFeatureSelect = useCallback((feature) => {
        setCoordsInfo(null); // Закрываем панель координат
        setActiveTool(null); // Деактивируем инструмент "Координаты"
        handleFeatureSelectForEdit(feature); // Открываем панель редактирования для выбранного объекта
    }, [handleFeatureSelectForEdit]);

    // Обработчик для инструмента "НСПД"
    const handleNSPDSelect = useCallback((info) => {
        setNspdInfo(info);
        setNSPDModalVisible(true); // Открываем модальное окно при клике на карту
        setActiveTool(null); // Деактивируем инструмент после клика
    }, []);

    const handleBulkUpdate = useCallback(async (updatedFeatures) => {
        try {
            const response = await fetch('/api/features/bulk-update', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ features: updatedFeatures })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка массового обновления');
            }

            toast.success(`Успешно обновлено ${updatedFeatures.length} объектов`);
            await fetchMetadata(); // Перезагружаем данные
        } catch (error) {
            console.error('Bulk update error:', error);
            toast.error(`Не удалось обновить объекты: ${error.message}`);
        }
    }, [fetchMetadata, legendData]);

    const handleShiftSelect = useCallback((featureId) => {
        setMapSelectedIds(prev => {
            if (prev.includes(featureId)) {
                return prev.filter(id => id !== featureId);
            } else {
                return [...prev, featureId];
            }
        });
    }, []);

    // Эффект для отслеживания отпускания Shift
    useEffect(() => {
        const handleKeyUp = (e) => {
            if (e.key === 'Shift') {
                setMapSelectedIds(ids => {
                    if (ids.length > 0) {
                        // Если есть выделенные объекты, открываем менеджер объектов
                        setObjectManagerVisible(true);
                        // Выбранные ID передадутся в ObjectManagerPanel через проп initialSelectedIds (нужно добавить)
                    }
                    return ids; // Оставляем их выделенными, чтобы передать в менеджер
                });
            }
        };

        window.addEventListener('keyup', handleKeyUp);
        return () => window.removeEventListener('keyup', handleKeyUp);
    }, []);

    // Сброс выделения при закрытии менеджера объектов
    useEffect(() => {
        if (!isObjectManagerVisible) {
            setMapSelectedIds([]);
        }
    }, [isObjectManagerVisible]);

    const handleBulkDelete = useCallback(async (featureIds) => {
        try {
            const response = await fetch('/api/features/bulk-delete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: featureIds })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка массового удаления');
            }

            toast.success(`Успешно удалено ${featureIds.length} объектов`);
            await fetchMetadata(); // Перезагружаем данные
        } catch (error) {
            console.error('Bulk delete error:', error);
            toast.error(`Не удалось удалить объекты: ${error.message}`);
        }
    }, [fetchMetadata, legendData]);

    // Стабилизируем объект с опциями для инструментов
    const toolOptions = useMemo(() => ({
        'measure-coords': { onCoordsSelect: handleCoordsSelect },
        'nspd-search': { onNSPDSelect: handleNSPDSelect }
    }), [handleCoordsSelect, handleNSPDSelect]); // Зависимость корректна

    // Стабилизируем фильтры по умолчанию, чтобы не создавать новый объект при каждом рендере
    const defaultActiveFilters = useMemo(() => {
        // В админ панели по умолчанию показываем все категории (берём из legendData)
        const allPlotIds = (legendData.plots || []).map(p => p.id);
        const allBuildingIds = (legendData.buildings || []).map(b => b.id);
        const allRoadIds = (legendData.roads || []).map(r => r.id);
        const allMarkerIds = (legendData.markers || []).map(m => m.id);
        
        return {
            plots: allPlotIds,
            buildings: allBuildingIds,
            roads: allRoadIds,
            markers: allMarkerIds,
            status: ['for_sale', 'is_building', 'is_planned', 'no_status']
        };
    }, [legendData]);

    // Если мы редактируем геометрию, все инструменты должны быть неактивны.
    // Если мы редактируем свойства, разрешаем использовать инструменты измерения.
    const isMeasuringWhileEditing = editingFeature && activeTool && activeTool.startsWith('measure-');
    const currentActiveTool = featureToEditGeometry ? null : 
                              isMeasuringWhileEditing ? activeTool :
                              editingFeature ? null : activeTool;

    return (
        <div className={styles.adminDashboardPage}>
            {/* Header */}
            <Header
                title={process.env.REACT_APP_TITLE || "IChis"} // Подгружаем заголовок из конфига
                subtitle={process.env.REACT_APP_SUBTITLE || "Админ панель"} // Подгружаем подзаголовок
                logo={process.env.REACT_APP_LOGO || "SPD"}
                navItems={navItems}
                toolbarTools={toolsConfig[activeMode]}
                activeTool={currentActiveTool}
                onToolChange={handleToolChange}
                actions={headerActions}
                user={{ name: 'Администратор', avatar: null }}
                sticky
            />

            <input
                type="file"
                ref={importInputRef}
                style={{ display: 'none' }}
                accept=".json,application/json"
                onChange={handleFileImport}
            />

            <div className={styles.mainContainer}>
                {/* Основная область */}
                <main className={styles.dashboardContainer}>
                     <MapViewer
                         printingSettings={(activeMode === 'printing' || activeMode === 'export') ? printingSettings : null}
                         mapData={mapData}
                         styleSwapControlProps={{
                             onClick: handleMapStyleChange,
                             title: `Стиль: ${mapStyleNames[mapStyleId]}`,
                             iconSvg: mapStyleIcons[mapStyleId]
                         }}
                         mapStyleId={mapStyleId}
                         activeTool={currentActiveTool}
                         toolOptions={toolOptions}
                         // Колбэки для обновления данных
                         onFeatureUpdate={handleFeatureUpdate}
                         onFeatureDelete={handleFeatureDelete}
                         onFeatureCreate={handleFeatureCreate}
                         onFeatureSelect={handleFeatureSelectForEdit}
                         featureToEditGeometry={featureToEditGeometry}
                         overlay={initialOverlay}
                         featureBeforeEdit={featureBeforeEdit}
                         mapRef={mapRef}
                         onOverlayUpdate={handleOverlayUpdate}
                         focusedFeature={editingFeature}
                         featureToCancel={featureToCancel}
                         onCancelComplete={useCallback(() => {
                             setFeatureToCancel(null); // Сбрасываем ID после удаления
                             setPreviousMapViewState(null); // Очищаем предыдущее состояние
                         }, [])}
                         onMapMove={setCurrentMapViewState} // Передаем колбэк для обновления текущего состояния карты
                          mapSelectedIds={mapSelectedIds}
                          onShiftSelect={handleShiftSelect}
                          isShiftSelectionEnabled={isSPDPlusActive}
                          restoreMapViewState={restoreMapViewState} // Передаем состояние для восстановления
                         onRestoreComplete={() => setRestoreMapViewState(null)} // Колбэк для сброса состояния восстановления
                         legendData={legendData}
                         activeFilters={defaultActiveFilters}
                         onToolDeactivateRequest={() => setActiveTool(null)}
                     />
                </main>
            </div>
            {/* Панель редактирования теперь рендерится всегда, а видимость управляется внутри нее */}

            <ActionToolbar
                isVisible={!!editingFeature && !featureToEditGeometry}
                onEditGeometry={handleEditGeometry}
                onDelete={handleDeleteFeature}
                onNSPDUpdate={() => setNSPDUpdateModalOpen(true)}
                hasCadastralNumber={!!editingFeature?.properties?.cadastral_number}
                hasNSPD={!!nspdModule}
            />

            <NSPDUpdateModal 
                isVisible={isNSPDUpdateModalOpen}
                onClose={() => setNSPDUpdateModalOpen(false)}
                feature={editingFeature}
                onUpdate={(updatedFeature) => {
                    handleEditingSave(updatedFeature);
                    setEditingFeature(updatedFeature);
                }}
            />

            <EditingPanel
                feature={editingFeature}
                onCancel={closeAllPanels}
                onSave={handleEditingSave}
                availableProperties={featureProperties}
                onLegendUpdate={fetchMetadata}
                legendData={legendData}
                mapData={mapData}
            />
            {console.log('AdminDashboardPage: Rendering Page. featuresCount:', mapData?.features?.length || 0, 'legendLoaded:', !!legendData, 'mode:', activeMode)}

            <LegendManager
                isVisible={isLegendManagerVisible}
                onClose={() => setLegendManagerVisible(false)}
                onSave={fetchMetadata} // Перезагружаем все метаданные после сохранения
            />

            {/* Панели инструментов из динамических модулей */}
            {modules.map(mod => {
                console.log(`Checking module: ${mod.id}`, { hasPanel: !!mod.Panel, activeMode });
                // Определяем, относится ли текущий режим к этому модулю
                const modTools = mod.getTools ? mod.getTools(iconComponents) : {};
                const isModeHandledByModule = activeMode === mod.id || Object.keys(modTools).includes(activeMode);
                const isNSPDModal = mod.id === 'nspd-integration' && isNSPDModalVisible;

                console.log(`Module ${mod.id} condition:`, { isModeHandledByModule, isNSPDModal });

                if (mod.Panel && (isModeHandledByModule || isNSPDModal)) {
                    const ModulePanel = mod.Panel;
                    return (
                        <ModulePanel 
                            key={mod.id}
                            isVisible={mod.id === 'nspd-integration' ? isNSPDModalVisible : true}
                            onClose={() => mod.id === 'nspd-integration' ? setNSPDModalVisible(false) : setActiveMode('drawing')}
                            mapData={mapData}
                            legendData={legendData}
                            activeTool={activeTool}
                            setActiveTool={setActiveTool}
                            nspdInfo={mod.id === 'nspd-integration' ? nspdInfo : null}
                            onMapSelectRequest={() => {
                                setNSPDModalVisible(false);
                                setActiveTool('nspd-search');
                            }}
                            activeFilters={defaultActiveFilters}
                            onImport={fetchMetadata}
                            onFeatureSelect={handleFeatureSelectFromManager}
                            mapRef={mapRef}
                        />
                    );
                }
                return null;
            })}

            <ObjectManagerPanel
                isVisible={isObjectManagerVisible}
                onClose={() => setObjectManagerVisible(false)}
                mapData={mapData}
                onFeatureSelect={handleFeatureSelectFromManager}
                focusedFeatureId={editingFeature?.id}
                onVisibilityChange={handleFeatureVisibilityChange}
                onBulkVisibilityChange={handleBulkVisibilityChange}
                onBulkUpdate={handleBulkUpdate}
                onBulkDelete={handleBulkDelete}
                initialSelectedIds={mapSelectedIds}
                onSelectionChange={setMapSelectedIds}
                isBulkEditEnabled={isSPDPlusActive}
                legendData={legendData}
            />

            <CoordsInfoPanel
                info={coordsInfo}
                onClose={() => {
                    setCoordsInfo(null);
                    // Если инструмент "Координаты" все еще активен, деактивируем его
                    if (activeTool === 'measure-coords') {
                        setActiveTool(null);
                    }
                }}
                onFeatureClick={handleCoordsFeatureSelect}
            />

            <ImportModal
                isVisible={isImportModalVisible}
                features={featuresToImport}
                onClose={() => {
                    setImportModalVisible(false);
                    setFeaturesToImport([]);
                }}
                onImport={handleImportConfirm}
            />
        </div>
    );
};

const AdminDashboardPage = () => {
    // Оборачиваем панель в провайдеры из модулей (если они есть)
    let content = <AdminDashboardPageInner />;
    
    // Важно: проходим в обратном порядке или учитываем вложенность
    modules.forEach(mod => {
        if (mod.Provider) {
            const Provider = mod.Provider;
            content = <Provider>{content}</Provider>;
        }
    });

    return (
        <AuthGuard>
            {content}
        </AuthGuard>
    );
};

export default AdminDashboardPage;
